import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip
} from '@mui/material';
import {
  Camera as CameraIcon,
  CameraAlt as CameraAltIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  Visibility as VisibilityIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import Webcam from 'react-webcam';
import { MaintenanceImage } from '../../utils/types';

interface CapturedImage {
  id: string;
  type: 'serial' | 'model' | 'defect';
  label: string;
  dataUrl: string;
  uploadedUrl?: string;
  timestamp: Date;
}

interface WebcamCaptureProps {
  open: boolean;
  onClose: () => void;
  onSave: (images: MaintenanceImage[]) => void;
  maintenanceId: string;
  deviceInfo?: {
    brand?: string;
    model?: string;
    serialNumber?: string;
  };
}

const WebcamCapture: React.FC<WebcamCaptureProps> = ({
  open,
  onClose,
  onSave,
  maintenanceId,
  deviceInfo
}) => {
  const webcamRef = useRef<Webcam>(null);
  const [capturedImages, setCapturedImages] = useState<CapturedImage[]>([]);
  const [currentCaptureType, setCurrentCaptureType] = useState<'serial' | 'model' | 'defect'>('serial');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [error, setError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');

  const captureTypeLabels = useMemo(() => ({
    serial: 'صورة الرقم التسلسلي والموديل',
    model: 'صورة العميل عند استلام الجهاز',
    defect: 'صورة العيب أو المشكلة'
  }), []);

  const captureTypeDescriptions = useMemo(() => ({
    serial: 'التقط صورة واضحة للرقم التسلسلي والموديل المكتوب على الجهاز',
    model: 'التقط صورة للعميل عند استلام الجهاز (في حالة وجود مشكلة)',
    defect: 'توضح العيب أو المشكلة الموجودة في الجهاز'
  }), []);

  const captureTypeColors = useMemo(() => ({
    serial: 'primary',
    model: 'secondary',
    defect: 'error'
  } as const), []);

  // إعدادات الكاميرا
  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: facingMode
  };

  // التقاط صورة مع فحص شامل للكاميرا
  const captureImage = useCallback(() => {
    // التحقق من وجود مرجع الكاميرا
    if (!webcamRef.current) {
      setError('❌ الكاميرا غير متاحة - تأكد من توصيل الكاميرا');
      return;
    }

    // التحقق من جاهزية الكاميرا
    if (!cameraReady) {
      setError('⏳ الكاميرا لم تكتمل التحميل بعد - انتظر قليلاً');
      return;
    }

    // محاولة التقاط الصورة
    let imageSrc: string | null = null;
    try {
      imageSrc = webcamRef.current.getScreenshot();
    } catch (captureError) {
      console.error('خطأ في التقاط الصورة:', captureError);
      setError('❌ فشل في التقاط الصورة - تحقق من الكاميرا');
      return;
    }

    // التحقق من نجاح التقاط الصورة
    if (!imageSrc) {
      setError('❌ لم يتم التقاط صورة - تأكد من عمل الكاميرا بشكل صحيح');
      return;
    }

    // التحقق من أن الصورة ليست فارغة أو سوداء
    if (imageSrc.length < 1000) { // صورة صغيرة جداً قد تكون فارغة
      setError('⚠️ الصورة الملتقطة فارغة - تحقق من إضاءة الكاميرا');
      return;
    }

    // إنشاء كائن الصورة
    const newImage: CapturedImage = {
      id: `${currentCaptureType}_${Date.now()}`,
      type: currentCaptureType,
      label: captureTypeLabels[currentCaptureType],
      dataUrl: imageSrc,
      timestamp: new Date()
    };

    setCapturedImages(prev => [...prev, newImage]);
    setError(null);

    console.log('✅ تم التقاط الصورة بنجاح:', {
      type: currentCaptureType,
      size: imageSrc.length,
      timestamp: new Date().toISOString()
    });
  }, [currentCaptureType, cameraReady, captureTypeLabels]);

  // حذف صورة
  const deleteImage = (imageId: string) => {
    setCapturedImages(prev => prev.filter(img => img.id !== imageId));
  };

  // تبديل الكاميرا
  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    setCameraReady(false);
    setCameraError(null);
    setError(null);
    console.log('🔄 تبديل الكاميرا...');
  };

  // رفع الصور
  const uploadImages = async () => {
    if (capturedImages.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      const uploadedImages: MaintenanceImage[] = [];

      for (const image of capturedImages) {
        setUploadProgress(prev => ({ ...prev, [image.id]: 0 }));

        try {
          // تحويل dataUrl إلى blob
          const response = await fetch(image.dataUrl);
          const blob = await response.blob();

          // إنشاء FormData
          const formData = new FormData();
          const fileName = `maintenance_${maintenanceId}_${image.type}_${Date.now()}.jpg`;
          formData.append('image', blob, fileName);

          // رفع الصورة (سيتم تحديد مزود الخدمة من الإعدادات)
          const uploadResponse = await uploadImageToProvider(formData, image.type);

          setUploadProgress(prev => ({ ...prev, [image.id]: 100 }));

          // تحويل إلى MaintenanceImage
          const maintenanceImage: MaintenanceImage = {
            id: image.id,
            type: image.type,
            label: image.label,
            url: image.dataUrl,
            uploadedUrl: uploadResponse.url,
            timestamp: image.timestamp,
            provider: uploadResponse.provider || 'imgbb', // استخدام المزود الفعلي من نتيجة الرفع
            metadata: {
              maintenanceId,
              imageType: image.type,
              timestamp: image.timestamp.toISOString()
            }
          };

          uploadedImages.push(maintenanceImage);
        } catch (uploadError) {
          console.error(`Error uploading ${image.type} image:`, uploadError);
          setUploadProgress(prev => ({ ...prev, [image.id]: -1 }));
        }
      }

      if (uploadedImages.length > 0) {
        onSave(uploadedImages);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setError('حدث خطأ أثناء رفع الصور');
    } finally {
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  // دالة رفع الصورة باستخدام الخدمة الموحدة
  const uploadImageToProvider = async (formData: FormData, imageType: string) => {
    try {
      // تحويل FormData إلى Blob
      const file = formData.get('image') as File;

      // استخدام الخدمة الموحدة
      const { uploadUnifiedImage } = await import('../../services/unifiedImageService');

      const result = await uploadUnifiedImage({
        file: file,
        context: 'maintenance',
        contextId: maintenanceId,
        imageType: imageType as 'serial' | 'model' | 'defect'
      });

      return {
        url: result.url,
        provider: result.provider // إرجاع المزود الفعلي
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  // حفظ الصور
  const handleSave = () => {
    if (capturedImages.length === 0) {
      setError('يجب التقاط صورة واحدة على الأقل');
      return;
    }

    uploadImages();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PhotoCameraIcon color="primary" />
            <Typography variant="h6">التقاط صور الجهاز</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* قسم الكاميرا */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
                  <Typography variant="h6" gutterBottom>
                    كاميرا الويب
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Tooltip title="تبديل الكاميرا">
                      <IconButton onClick={switchCamera} color="primary">
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* نوع الصورة المراد التقاطها */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>نوع الصورة</InputLabel>
                  <Select
                    value={currentCaptureType}
                    onChange={(e) => setCurrentCaptureType(e.target.value as any)}
                    label="نوع الصورة"
                  >
                    <MenuItem value="serial">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          📱 صورة الرقم التسلسلي والموديل
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          التقط صورة واضحة للرقم التسلسلي والموديل
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="defect">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          ⚠️ صورة العيب أو المشكلة
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          توضح العيب أو المشكلة في الجهاز
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="model">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          👤 صورة العميل عند استلام الجهاز
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          في حالة وجود مشكلة - صورة العميل للتوثيق
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* وصف نوع الصورة المحدد */}
                <Box sx={{ mt: 1, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.dark" sx={{ fontWeight: 'medium' }}>
                    📋 {captureTypeLabels[currentCaptureType]}
                  </Typography>
                  <Typography variant="caption" color="info.dark" sx={{ mt: 0.5, display: 'block' }}>
                    {captureTypeDescriptions[currentCaptureType]}
                  </Typography>
                </Box>

                {/* عرض الكاميرا */}
                <Box 
                  sx={{ 
                    position: 'relative',
                    width: '100%',
                    height: 400,
                    bgcolor: 'black',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    videoConstraints={videoConstraints}
                    onUserMedia={() => {
                      setCameraReady(true);
                      setCameraError(null);
                      console.log('✅ تم تشغيل الكاميرا بنجاح');
                    }}
                    onUserMediaError={(error: any) => {
                      setCameraReady(false);
                      setCameraError('فشل في الوصول للكاميرا');
                      console.error('❌ خطأ في الكاميرا:', error);
                      setError('❌ لا يمكن الوصول للكاميرا - تأكد من توصيلها والسماح بالوصول إليها');
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  
                  {!cameraReady && (
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      sx={{ transform: 'translate(-50%, -50%)', textAlign: 'center' }}
                    >
                      {cameraError ? (
                        <>
                          <Typography variant="h6" color="error" mb={2}>
                            ❌ خطأ في الكاميرا
                          </Typography>
                          <Typography variant="body2" color="white" mb={2}>
                            {cameraError}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            تأكد من توصيل الكاميرا والسماح بالوصول إليها
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CircularProgress color="primary" />
                          <Typography variant="body2" color="white" mt={1}>
                            جاري تحميل الكاميرا...
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}

                  {/* زر التقاط الصورة */}
                  {cameraReady ? (
                    <Box
                      position="absolute"
                      bottom={16}
                      left="50%"
                      sx={{ transform: 'translateX(-50%)' }}
                    >
                      <Button
                        variant="contained"
                        color={captureTypeColors[currentCaptureType]}
                        size="large"
                        startIcon={<CameraAltIcon />}
                        onClick={captureImage}
                        sx={{
                          borderRadius: '50px',
                          px: 3,
                          py: 1.5,
                          fontWeight: 'bold'
                        }}
                      >
                        التقاط {captureTypeLabels[currentCaptureType]}
                      </Button>
                    </Box>
                  ) : !cameraError && (
                    <Box
                      position="absolute"
                      bottom={16}
                      left="50%"
                      sx={{ transform: 'translateX(-50%)' }}
                    >
                      <Button
                        variant="outlined"
                        disabled
                        size="large"
                        startIcon={<CameraAltIcon />}
                        sx={{
                          borderRadius: '50px',
                          px: 3,
                          py: 1.5,
                          fontWeight: 'bold'
                        }}
                      >
                        انتظار الكاميرا...
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* معلومات الجهاز */}
                {deviceInfo && (
                  <Box mt={2} p={2} bgcolor="background.default" borderRadius={1}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      معلومات الجهاز:
                    </Typography>
                    <Grid container spacing={1}>
                      {deviceInfo.brand && (
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">الماركة:</Typography>
                          <Typography variant="body2">{deviceInfo.brand}</Typography>
                        </Grid>
                      )}
                      {deviceInfo.model && (
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">الموديل:</Typography>
                          <Typography variant="body2">{deviceInfo.model}</Typography>
                        </Grid>
                      )}
                      {deviceInfo.serialNumber && (
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">الرقم التسلسلي:</Typography>
                          <Typography variant="body2">{deviceInfo.serialNumber}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* قسم الصور الملتقطة */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  الصور الملتقطة ({capturedImages.length})
                </Typography>

                {capturedImages.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <CameraIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      لم يتم التقاط أي صور بعد
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {capturedImages.map((image) => (
                      <Card key={image.id} sx={{ mb: 2 }}>
                        <CardMedia
                          component="img"
                          height="120"
                          image={image.dataUrl}
                          alt={image.label}
                        />
                        <CardContent sx={{ p: 1 }}>
                          <Box display="flex" alignItems="center" justifyContent="between">
                            <Box>
                              <Chip
                                label={image.label}
                                color={captureTypeColors[image.type]}
                                size="small"
                                sx={{ mb: 1 }}
                              />
                              <Typography variant="caption" display="block" color="text.secondary">
                                {image.timestamp.toLocaleTimeString()}
                              </Typography>
                            </Box>
                            <Box>
                              {uploadProgress[image.id] !== undefined && (
                                <Box sx={{ position: 'relative', display: 'inline-flex', mr: 1 }}>
                                  <CircularProgress
                                    variant="determinate"
                                    value={uploadProgress[image.id] === -1 ? 0 : uploadProgress[image.id]}
                                    size={20}
                                    color={uploadProgress[image.id] === -1 ? 'error' : 'primary'}
                                  />
                                </Box>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => deleteImage(image.id)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>إلغاء</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={isUploading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={capturedImages.length === 0 || isUploading}
        >
          {isUploading ? 'جاري الرفع...' : 'حفظ الصور'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebcamCapture;
